import dayjs from 'dayjs'

/**
 * Statuses whose rows go red once they've been sitting 24 hours or more.
 *
 * The client's wording was "status `Closed`, or no status". `Not Started` IS the
 * "no status" case: `RepairOrder.status` is non-nullable and defaults to it, so a row
 * with literally no status can't exist. Dropping `Not Started` from this list would
 * also silently undo the original behavior of this highlight, which existed to flag
 * exactly those untouched intake claims — the `Closed` case is what was *added* here.
 * (The `!row.status` check below is belt-and-braces for a row that arrives without the
 * field at all, e.g. a partial payload.)
 *
 * An allowlist rather than an exclusion list: only the statuses the client named
 * highlight, so the resolved statuses (Paid / Written Off / Paid with Adjustments)
 * stay unhighlighted without depending on anyone remembering to exclude them when a
 * new status is added. `Closed — Submitted` is explicitly out — the claim has moved
 * on, so the intake clock no longer applies. `On Outstanding List` is out too, but
 * only once the OL row is real; see `isOutstandingListDraft`.
 */
export const OVERDUE_HIGHLIGHT_STATUSES = ['Not Started', 'Closed']

export const OVERDUE_AFTER_HOURS = 24

/**
 * A claim showing as `On Outstanding List — Draft`: the status has moved but the OL
 * row is still a draft, so nobody is working it yet.
 *
 * Exported because `constants.jsx` renders the same combination as its own display
 * status, and the two must not be able to drift apart.
 */
export const isOutstandingListDraft = (row) =>
  row.status === 'On Outstanding List' && row.state === 'Draft'

/**
 * Whether the intake clock is still running for a row.
 *
 * Keys off the row's own status and state, never off which accordion section it
 * happens to be rendered in. A draft OL row counts: putting a claim on the list is
 * what stops the clock, and a draft hasn't done that yet.
 */
const clockIsRunning = (row) =>
  !row.status || OVERDUE_HIGHLIGHT_STATUSES.includes(row.status) || isOutstandingListDraft(row)

/**
 * An RO is overdue once it has been at least 24 hours since it was received and it
 * hasn't moved on to a status that stops the clock. Exactly 24 hours counts as overdue.
 * The clock starts from `receivedAt` (which may be backdated by the worker on Add) —
 * not from `createdAt`, which is the row's system insert time.
 *
 * Lives apart from `constants.jsx` so it can be tested as the pure predicate it is,
 * without pulling the cell components (and the store graph behind them) into scope.
 */
export const isIntakeOverdue = (row) =>
  Boolean(row.receivedAt) &&
  clockIsRunning(row) &&
  dayjs().diff(dayjs(row.receivedAt), 'hour') >= OVERDUE_AFTER_HOURS
